(() => {
  if (customElements.get('ll-heat-guide')) return;

  customElements.define('ll-heat-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.video = this.querySelector('[data-heat-video]');
      this.posterElement = this.querySelector('[data-heat-poster]');
      this.playButton = this.querySelector('[data-heat-play]');
      this.captionElement = this.querySelector('[data-heat-caption]');
      this.ccButton = this.querySelector('[data-heat-cc]');
      this.ccState = this.querySelector('[data-heat-cc-state]');
      this.chapterButtons = [...this.querySelectorAll('[data-heat-chapter]')];
      this.videoStage = this.querySelector('.ll-heat__video-stage');
      if (!this.video) return;

      this.captionsOn = true;
      this.playbackStarted = false;
      this.attachCaptions();
      this.bindPlayer();
      this.bindRevealMotion();
      this.updateChapter(0);
    }

    disconnectedCallback() {
      this.revealObserver?.disconnect();
    }

    attachCaptions() {
      const dataElement = this.querySelector('[data-heat-captions]');
      if (!dataElement) return;
      try {
        const data = JSON.parse(dataElement.textContent);
        this.cues = (Array.isArray(data.cues) ? data.cues : []).map(cue => ({
          start: Number(cue.start),
          end: Number(cue.end),
          text: String(cue.text || '')
        })).filter(cue => Number.isFinite(cue.start) && Number.isFinite(cue.end) && cue.end > cue.start && cue.text);
        this.updateCaption(0);
      } catch (error) {
        this.cues = [];
        if (this.captionElement) this.captionElement.hidden = true;
      }
    }

    bindPlayer() {
      this.playButton?.addEventListener('click', () => this.startPlayback());
      this.chapterButtons.forEach(button => button.addEventListener('click', () => this.seekTo(Number(button.dataset.heatTime))));
      this.querySelectorAll('[data-heat-watch]').forEach(button => button.addEventListener('click', () => this.seekTo(Number(button.dataset.heatTime), true)));
      ['timeupdate', 'seeking', 'loadedmetadata'].forEach(eventName => this.video.addEventListener(eventName, () => {
        this.updateChapter(this.video.currentTime);
        this.updateCaption(this.video.currentTime);
      }));

      this.ccButton?.addEventListener('click', () => {
        this.captionsOn = !this.captionsOn;
        this.ccButton.setAttribute('aria-pressed', String(this.captionsOn));
        this.ccState.textContent = this.captionsOn ? this.ccButton.dataset.onLabel : this.ccButton.dataset.offLabel;
        this.updateCaption(this.video.currentTime);
      });

      if (this.ccButton) {
        this.ccButton.dataset.onLabel = this.ccState?.textContent || 'On';
        this.ccButton.dataset.offLabel = this.ccButton.getAttribute('data-off-label') || 'Off';
      }
    }

    startPlayback() {
      this.video.controls = true;
      this.video.muted = false;
      this.video.defaultMuted = false;
      this.video.volume = 1;
      this.playbackStarted = true;
      if (this.playButton) this.playButton.hidden = true;
      if (this.posterElement) this.posterElement.hidden = true;
      this.updateCaption(this.video.currentTime);
      const promise = this.video.play();
      if (promise?.catch) promise.catch(() => {
        this.playbackStarted = false;
        if (this.playButton) this.playButton.hidden = false;
        if (this.posterElement) this.posterElement.hidden = false;
        this.updateCaption(this.video.currentTime);
      });
    }

    seekTo(time, scrollToVideo = false) {
      if (!Number.isFinite(time)) return;
      const applySeek = () => {
        const duration = Number.isFinite(this.video.duration) ? this.video.duration : time;
        this.video.currentTime = Math.min(Math.max(time, 0), duration);
        this.updateChapter(time);
        this.startPlayback();
      };
      if (scrollToVideo) {
        const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.videoStage?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
      if (this.video.readyState >= 1) applySeek();
      else {
        this.video.addEventListener('loadedmetadata', applySeek, { once: true });
        this.video.load();
      }
    }

    updateChapter(time) {
      if (!this.chapterButtons.length) return;
      let active = this.chapterButtons[0];
      this.chapterButtons.forEach(button => {
        if (time >= Number(button.dataset.heatTime)) active = button;
      });
      this.chapterButtons.forEach(button => {
        const selected = button === active;
        button.classList.toggle('is-active', selected);
        if (selected) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    updateCaption(time) {
      if (!this.captionElement) return;
      const activeCue = this.captionsOn && this.playbackStarted && this.cues?.find(cue => time >= cue.start && time < cue.end);
      this.captionElement.hidden = !activeCue;
      this.captionElement.textContent = activeCue?.text || '';
    }

    bindRevealMotion() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        this.querySelectorAll('[data-heat-reveal]').forEach(element => element.classList.add('is-visible'));
        return;
      }
      this.classList.add('is-heat-enhanced');
      this.revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
      this.querySelectorAll('[data-heat-reveal]').forEach(element => this.revealObserver.observe(element));
    }
  });
})();
